import { useEffect, useRef } from 'react';
import { parseISO, isValid, subMinutes, differenceInMilliseconds, formatISO, differenceInMinutes } from 'date-fns';

/**
 * Hook to schedule a query refetch at a specific time before a match date
 * 
 * @param {Object} options - Configuration options
 * @param {Date|string} options.matchDate - The match date (ISO string or Date object)
 * @param {number} options.minutesBefore - Minutes before match to schedule refetch (default: 60)
 * @param {Array} options.queryKey - The React Query key to invalidate
 * @param {Object} options.queryClient - The React Query client instance to use
 * @param {boolean} options.enabled - Whether the scheduling is enabled
 * @param {Function} options.onSchedule - Optional callback when a refetch is scheduled
 * @param {Function} options.onRefetch - Optional callback when the refetch occurs
 * @returns {{ scheduledTime: Date|null }} The scheduled refetch time if any
 */
const useScheduledMatchRefetch = ({
  matchDate,
  minutesBefore = 60,
  queryKey,
  queryClient,
  enabled = true,
  onSchedule,
  onRefetch
}) => {
  const refetchTimeoutRef = useRef(null);
  const scheduledTimeRef = useRef(null);

  if (!queryClient && enabled) {
    console.warn('useScheduledMatchRefetch: queryClient is required when enabled is true');
    return { scheduledTime: null };
  }

  useEffect(() => {
    // Clear any existing timeout when dependencies change
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = null;
      scheduledTimeRef.current = null;
    }

    if (!enabled || !matchDate) return;

    // Parse the match date if it's a string
    let matchTime;
    try {
      matchTime = matchDate instanceof Date ? matchDate : parseISO(matchDate);
    } catch (error) {
      console.error('Error parsing match date:', error);
      return;
    }

    const now = new Date();

    // Validate the match time
    if (!isValid(matchTime)) {
      console.warn('Invalid match date provided to useScheduledMatchRefetch:', matchDate);
      return;
    }

    // Calculate the scheduled refetch time
    const scheduledRefetchTime = subMinutes(matchTime, minutesBefore);
    scheduledTimeRef.current = scheduledRefetchTime;

    // Only schedule if the refetch time is in the future
    const timeUntilRefetch = differenceInMilliseconds(scheduledRefetchTime, now);

    // Only refetch if the time until the match is less than an hour (3,600,000 ms)
    const ONE_HOUR_MS = 3600000;

    if (timeUntilRefetch > 0 && timeUntilRefetch <= ONE_HOUR_MS) {
      console.log(`Match time: ${formatISO(matchTime)}`);
      console.log(`Scheduling refetch for: ${formatISO(scheduledRefetchTime)}, in ${differenceInMinutes(scheduledRefetchTime, now)} minutes`);
      console.log(`Time until refetch: ${timeUntilRefetch}ms (${timeUntilRefetch / 60000} minutes)`);

      onSchedule?.({
        matchTime,
        scheduledRefetchTime,
        timeUntilRefetch
      });

      // Set the timeout
      refetchTimeoutRef.current = setTimeout(() => {
        console.log('Executing scheduled refetch');

        if (queryKey) {
          queryClient.invalidateQueries(queryKey);
        }

        onRefetch?.();

        // Clean up
        scheduledTimeRef.current = null;
        refetchTimeoutRef.current = null;
      }, timeUntilRefetch);
    } else if (timeUntilRefetch > ONE_HOUR_MS) {
      console.log(`Match is more than an hour away (${timeUntilRefetch / 60000} minutes), not scheduling refetch yet`);
    } else {
      console.log(`Scheduled refetch time (${formatISO(scheduledRefetchTime)}) is in the past, not scheduling`);
    }

    // Cleanup function
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = null;
      }
    };
  }, [matchDate, minutesBefore, queryKey, enabled, queryClient, onSchedule, onRefetch]);

  return {
    scheduledTime: scheduledTimeRef.current
  };
};

export default useScheduledMatchRefetch;
